import type { Badges } from './moderation';
import type { OnboardingStage } from './onboarding';

export interface MeUser {
  id: number;
  email: string;
  name: string;
  role: 'creator' | 'superadmin';
  /** Every community this account holds. Authoritative — there is no singular
   *  `skool_id` on the v1 body; the SELECTED one is `Bootstrap.selected_skool_id`. */
  skool_ids: number[];
  plan_status: AccountPlanStatus;
  /** UTC-aware ISO instant, or null. Parse with `parseTs`. */
  trial_ends_at: string | null;
  pending_skool_url: string | null;
  onboarding_stage: OnboardingStage;
}

/**
 * `/v1/me` — who is asking, and what they hold.
 *
 * The SHAPES and the date-bound helper, not the session. Caching the bootstrap,
 * redirecting an expired plan and reporting to analytics are things an app does with
 * this answer; `web-app/src/lib/session.ts` does them.
 */

/** Derived ACCOUNT-level plan state (`domain.billing.account_plan_status`). Same
 *  values as `AdminUser['plan_status']`, non-optional here because `/v1/me` always
 *  sends it. `free_for_life` is the hand-granted comp (`users.paid_at`) — never
 *  `paid`, which is reserved for a real subscription. */
export type AccountPlanStatus =
  | 'legacy'
  | 'trial'
  | 'expired'
  | 'paid'
  | 'free_for_life';

/** The fuller community — `CommunityRef` plus the per-skool billing and roster
 *  state. Structurally the same object `lib/admin.ts`'s `Community` describes, so
 *  it drops straight into `community.list` and into `CommunitySelect`. */
export interface MeCommunity {
  id: number;
  slug: string;
  name: string;
  logo_url: string | null;
  total_members: number | null;
  total_courses: number | null;
  plan_status: 'paid' | 'free' | 'trial' | 'expired' | 'legacy' | 'needs_payment';
  trial_ends_at: string | null;
  roster_state: 'ok' | 'ask' | 'escalate' | 'unknown';
  roster_role: 'owner' | 'admin' | 'moderator' | null;
}

export interface Bootstrap {
  user: MeUser;
  /** The communities the caller HOLDS — never the fleet, even for a superadmin.
   *  The superadmin's cross-fleet picker is `GET /v1/communities?q=`, a different
   *  route with a different job. */
  communities: MeCommunity[];
  /** The server's answer to "which community am I looking at". It confirms the id
   *  the client sent, or names the one it would have picked. Null only when the
   *  account holds none. */
  selected_skool_id: number | null;
  /** Present only when `skool_id` was sent. Absent and null are different answers,
   *  which is why this is optional rather than nullable. */
  badges?: Badges;
  /** The server's clock, so "3 minutes ago" is measured against it rather than
   *  against a browser clock that may be minutes out. */
  server_time: string;
}

/** UTC ISO instant of the START of a LOCAL day. No arg = today; pass 'YYYY-MM-DD'
 *  for a specific local calendar day. Data is stored UTC, so this is the correct
 *  UTC boundary for filtering on the viewer's local day whatever their offset.
 *
 *  Idempotent: a value that is already an instant (contains 'T') is returned
 *  unchanged, so a range that arrived from a deep-link as an explicit instant is
 *  never re-interpreted as a local day. */
export function localDayStartISO(day?: string): string {
  if (day && day.includes('T')) return day;
  const d = day ? new Date(day + 'T00:00:00') : new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

